import { createFileRoute } from '@tanstack/react-router';
import { AlertTriangle, CheckCircle2, Loader2 } from 'lucide-react';
import { Button } from '@/components/atoms/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/atoms/card';
import { PublicLayout } from '@/components/public/PublicLayout';
import { useEmergencyAcknowledgement } from '@/hooks/public/useEmergencyAcknowledgement';

export const Route = createFileRoute('/(public)/e/$token')({
  component: EmergencyAcknowledgementPage,
});

function formatWhen(value: string, timeZone: string) {
  return new Intl.DateTimeFormat('en-US', {
    timeZone,
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(new Date(value));
}

function EmergencyAcknowledgementPage() {
  const { token } = Route.useParams();
  const { data, isLoading, isError, acknowledge } = useEmergencyAcknowledgement(token);

  if (isLoading) {
    return (
      <PublicLayout maxWidth="sm">
        <div className="flex justify-center py-24">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </PublicLayout>
    );
  }

  if (isError || !data) {
    return (
      <PublicLayout maxWidth="sm">
        <Card>
          <CardHeader>
            <CardTitle>Link unavailable</CardTitle>
            <CardDescription>
              This emergency acknowledgement link is invalid or no longer available.
            </CardDescription>
          </CardHeader>
        </Card>
      </PublicLayout>
    );
  }

  const acknowledged = data.status === 'ACKNOWLEDGED';
  const actionable = !acknowledged && data.status !== 'CANCELLED' && !data.isExpired;

  return (
    <PublicLayout maxWidth="sm">
      <Card className="mt-8 overflow-hidden">
        <CardHeader className={acknowledged ? 'bg-emerald-50' : 'bg-amber-50'}>
          <div className="flex items-center gap-3">
            {acknowledged ? (
              <CheckCircle2 className="h-7 w-7 text-emerald-600" />
            ) : (
              <AlertTriangle className="h-7 w-7 text-amber-600" />
            )}
            <div>
              <CardTitle>
                {acknowledged ? 'Emergency acknowledged' : 'Emergency assignment'}
              </CardTitle>
              <CardDescription>{data.tenantName}</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-5">
          <div>
            <p className="text-lg font-semibold">{data.appointment.serviceName}</p>
            <p className="text-sm text-muted-foreground">{data.appointment.customerName}</p>
          </div>
          <dl className="space-y-3 text-sm">
            <div>
              <dt className="font-medium">When</dt>
              <dd className="text-muted-foreground">
                {formatWhen(data.appointment.startAt, data.appointment.timezone)}
              </dd>
            </div>
            <div>
              <dt className="font-medium">Address</dt>
              <dd className="text-muted-foreground">{data.appointment.address}</dd>
            </div>
            <div>
              <dt className="font-medium">Assigned technician</dt>
              <dd className="text-muted-foreground">{data.technicianName}</dd>
            </div>
          </dl>
          {data.status === 'ESCALATED' ? (
            <p className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
              This assignment has already escalated
              {data.supervisorName ? ` to ${data.supervisorName}` : ''}. You can still acknowledge
              it now.
            </p>
          ) : null}
          {data.isExpired && !acknowledged ? (
            <p className="text-sm text-destructive">
              This acknowledgement window has expired. Contact dispatch directly.
            </p>
          ) : null}
          {acknowledge.isError ? (
            <p className="text-sm text-destructive">
              We could not record the acknowledgement. Please try again or contact dispatch.
            </p>
          ) : null}
        </CardContent>
        {actionable ? (
          <CardFooter className="border-t">
            <Button
              className="w-full"
              size="lg"
              disabled={acknowledge.isPending}
              onClick={() => acknowledge.mutate()}
            >
              {acknowledge.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              Acknowledge emergency assignment
            </Button>
          </CardFooter>
        ) : null}
      </Card>
    </PublicLayout>
  );
}
